import React, { Suspense } from 'react';
import { List, Skeleton, Tabs, Tooltip } from 'antd';
import classNames from 'classnames';
import { AssetItemDto, AssetVersionItemDto } from '@api/Api';
import { LgWidth, MdWidth } from '@components/Responsive';
import Details from '@pages/MediaViewer/Details';
import { MediaViewerOutletContextState } from '@layouts/types';
import { useMediaViewerMobileActionVisibility } from '@pages/MediaViewer/components/MediaViewerMobileAction/MediaViewerMobileAction';
import useOnboardingProcess from '@hooks/useOnboardingProcess';

const Comments = React.lazy(() => import('@pages/MediaViewer/Comments'));
const Tasks = React.lazy(() => import('@pages/MediaViewer/Tasks'));

const sidebarPanelSuspenseFallback = (
  <List
    className="media_viewer_list-skeleton"
    itemLayout="horizontal"
    style={{ padding: 16, width: '100%' }}
    bordered={false}
    split={false}
    dataSource={new Array(10).fill(undefined).map((_, i) => ({ key: i }))}
    renderItem={() => (
      <List.Item>
        <Skeleton.Input style={{ height: 88 }} active />
      </List.Item>
    )}
  />
);

type MediaViewerSidebarProps = {
  sidebarRef: React.RefObject<HTMLDivElement>;
  selectedCollapse: string | null;
  isPanelPinned: boolean;
  isShowFloatCommentForm: boolean;
  isShowFloatCommentItem: boolean;
  onToggleOpenSelectedCollapse: (value: string | null) => void;
  onTogglePanelPin: () => void;
  setSelectedCollapse: (value: string | null) => void;
  sidebarMenuItems: {
    key: string;
    icon: React.ReactNode;
    className?: string;
    hasUnreadComments?: boolean;
    tooltip: React.ReactNode;
  }[];
  canViewTasks: boolean;
  canCreateTask: boolean;
  selectedVersion: AssetVersionItemDto;
  asset: AssetItemDto;
  commentsList: MediaViewerOutletContextState['commentsList'];
  isLoadingCommentsList: boolean;
  commentMentions: boolean;
  tasksCount: number;
  onSidebarHandlePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onSidebarHandlePointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onSidebarHandlePointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  isEmbeddedWebView: boolean;
};

function MediaViewerSidebar({
  isEmbeddedWebView,
  sidebarRef,
  selectedCollapse,
  isPanelPinned,
  isShowFloatCommentForm,
  isShowFloatCommentItem,
  onToggleOpenSelectedCollapse,
  onTogglePanelPin,
  setSelectedCollapse,
  sidebarMenuItems,
  canViewTasks,
  canCreateTask,
  selectedVersion,
  asset,
  commentsList,
  isLoadingCommentsList,
  commentMentions,
  tasksCount,
  onSidebarHandlePointerDown,
  onSidebarHandlePointerMove,
  onSidebarHandlePointerUp
}: MediaViewerSidebarProps) {
  const { tourData, goToStep } = useOnboardingProcess();
  const isAnnouncementMediaViewerTour =
    tourData?.type === 'announcement-media-viewer';
  const isOpenTooltip = isAnnouncementMediaViewerTour
    ? goToStep === 1
    : undefined;
  const mobileActionVisibility = useMediaViewerMobileActionVisibility({
    isShowFloatCommentForm,
    isShowFloatCommentItem
  });

  if (mobileActionVisibility && !mobileActionVisibility.showSidebar)
    return null;

  return (
    <div ref={sidebarRef} className={classNames('media_viewer_sidebar')}>
      <LgWidth>
        <div
          className={classNames(
            'media_viewer_sidebar-menu',
            'tour-announcement-media-viewer-sidebar-menu',
            {
              'tour-announcement-media-viewer-sidebar-menu-active':
                isAnnouncementMediaViewerTour && goToStep === 1
            }
          )}
        >
          {sidebarMenuItems.map((item) => (
            <Tooltip
              key={item.key}
              title={
                <div
                  className="media-viewer-tooltip-inner"
                  style={{ padding: 8 }}
                >
                  {item.tooltip}
                </div>
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="right"
              overlayClassName="toggle_favorite_overlay"
              open={isOpenTooltip}
            >
              <div className="media_viewer_sidebar-menu-item">
                <div
                  onClick={() => onToggleOpenSelectedCollapse(item.key)}
                  className={classNames(
                    'media_viewer_sidebar-menu-btn',
                    item.className,
                    {
                      active: item.key === selectedCollapse
                    }
                  )}
                >
                  {item.icon}
                  {item.hasUnreadComments && (
                    <span className="media_viewer_sidebar-menu-badge" />
                  )}
                </div>
              </div>
            </Tooltip>
          ))}
        </div>
        <div
          className={classNames('media_viewer_sidebar-content', {
            'b-open': !!selectedCollapse
          })}
        >
          <Suspense fallback={sidebarPanelSuspenseFallback}>
            {selectedCollapse === 'comments' && (
              <Comments
                canCreateTask={canCreateTask}
                selectedVersion={selectedVersion}
                commentsList={commentsList}
                isLoadingCommentsList={isLoadingCommentsList}
                asset={asset}
                onClosePanel={() => onToggleOpenSelectedCollapse(null)}
                commentMentions={commentMentions}
                isPanelPinned={isPanelPinned}
                onTogglePanelPin={onTogglePanelPin}
                setSelectedCollapse={setSelectedCollapse}
              />
            )}

            {selectedCollapse === 'tasks' && canViewTasks && (
              <Tasks
                onToggleOpenSelectedCollapse={onToggleOpenSelectedCollapse}
                canCreateTask={canCreateTask}
                selectedVersion={selectedVersion}
                asset={asset}
                isPanelPinned={isPanelPinned}
                onTogglePanelPin={onTogglePanelPin}
              />
            )}
            {selectedCollapse === 'fileInfo' && (
              <Details
                onClosePanel={() => onToggleOpenSelectedCollapse(null)}
                isPanelPinned={isPanelPinned}
                onTogglePanelPin={onTogglePanelPin}
              />
            )}
          </Suspense>
        </div>
      </LgWidth>
      <MdWidth>
        <div
          className="media_viewer_sidebar-bottom-sheet"
          onPointerDown={onSidebarHandlePointerDown}
          onPointerMove={onSidebarHandlePointerMove}
          onPointerUp={onSidebarHandlePointerUp}
          onPointerCancel={onSidebarHandlePointerUp}
        >
          <div className="media_viewer_sidebar-bottom-sheet-line" />
        </div>
        <Tabs
          activeKey={selectedCollapse || 'comments'}
          onChange={setSelectedCollapse}
          onTabClick={setSelectedCollapse}
        >
          <Tabs.TabPane
            tab={
              <>
                Comment{' '}
                {commentsList?.length ? (
                  <span>{commentsList?.length}</span>
                ) : null}
              </>
            }
            key="comments"
          >
            <Suspense fallback={sidebarPanelSuspenseFallback}>
              <Comments
                canCreateTask={canCreateTask}
                selectedVersion={selectedVersion}
                commentsList={commentsList}
                isLoadingCommentsList={isLoadingCommentsList}
                asset={asset}
                onClosePanel={() => onToggleOpenSelectedCollapse(null)}
                commentMentions={commentMentions}
                setSelectedCollapse={setSelectedCollapse}
              />
            </Suspense>
          </Tabs.TabPane>

          {canViewTasks && !isEmbeddedWebView && (
            <Tabs.TabPane
              tab={<>Tasks {tasksCount ? <span>{tasksCount}</span> : null}</>}
              key="tasks"
            >
              <Suspense fallback={sidebarPanelSuspenseFallback}>
                <Tasks
                  onToggleOpenSelectedCollapse={onToggleOpenSelectedCollapse}
                  canCreateTask={canCreateTask}
                  selectedVersion={selectedVersion}
                  asset={asset}
                />
              </Suspense>
            </Tabs.TabPane>
          )}
          <Tabs.TabPane tab="Details" key="details">
            <Suspense fallback={sidebarPanelSuspenseFallback}>
              <Details />
            </Suspense>
          </Tabs.TabPane>
        </Tabs>
      </MdWidth>
    </div>
  );
}

export default MediaViewerSidebar;
